import type {
  AnalyticsApiStatus,
  AnalyticsDateRange,
  AnalyticsOverviewApiResponse,
  AnalyticsTelemetryApiResponse,
  AnalyticsTelemetryApiRow,
  AnalyticsConversationInteractionRow,
  TelemetryOverviewStats,
  TelemetrySession,
  TelemetryStatus,
} from '~/modules/query/ai/types';
import { formatLocalTime } from '~/lib/datetime';

export const TELEMETRY_STATUSES: TelemetryStatus[] = [
  'Completed',
  'Abandoned',
  'Active',
  'Error',
  'Throttled',
  'Blocked',
];

export const API_STATUS_TO_TELEMETRY_STATUS: Record<AnalyticsApiStatus, TelemetryStatus> = {
  active: 'Active',
  completed: 'Completed',
  abandoned: 'Abandoned',
  error: 'Error',
  throttled: 'Throttled',
  blocked: 'Blocked',
};

export const TELEMETRY_STATUS_TO_API_STATUS: Record<TelemetryStatus, AnalyticsApiStatus> = {
  Active: 'active',
  Completed: 'completed',
  Abandoned: 'abandoned',
  Error: 'error',
  Throttled: 'throttled',
  Blocked: 'blocked',
};

/** What each status means, for a legend tooltip. Color lives in the chart theme. */
export const TELEMETRY_STATUS_DESCRIPTIONS: Record<TelemetryStatus, string> = {
  Completed:
    'The user successfully generated and saved, copied, or applied the AI response to their workflow. This indicates a valuable interaction.',
  Abandoned:
    'The user initiated an AI session but ultimately closed the window, hit cancel, or discarded the text without saving it.',
  Active:
    'The user currently has the AI interface open and is actively chatting, iterating, or waiting for an API response.',
  Error: 'The request failed to generate a response due to a technical failure or API timeout.',
  Throttled:
    'The request was intentionally blocked by the AI Manager because the user exceeded their configured daily or monthly token limits.',
  Blocked: 'The request was stopped by a safety guardrail or content moderation filter.',
};

const BUCKET_LABEL: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

/** Maps the overview wire response into the stats shape the Overview tab renders. */
export function mapAnalyticsOverview(
  apiData: AnalyticsOverviewApiResponse,
): TelemetryOverviewStats {
  const requestStatusBreakdown = (
    Object.keys(API_STATUS_TO_TELEMETRY_STATUS) as AnalyticsApiStatus[]
  ).reduce(
    (breakdown, apiStatus) => {
      breakdown[API_STATUS_TO_TELEMETRY_STATUS[apiStatus]] =
        apiData.statusBreakdown[apiStatus] ?? 0;
      return breakdown;
    },
    {} as TelemetryOverviewStats['requestStatusBreakdown'],
  );

  return {
    tokenConsumption: apiData.tokenConsumption.map((bucket) => ({
      intervalLabel: formatLocalTime(bucket.bucketStart, BUCKET_LABEL),
      inputTokens: bucket.inputTokens,
      outputTokens: bucket.outputTokens,
      cachedTokens: bucket.cachedTokens,
    })),
    estimatedCost: apiData.estimatedCost.map((bucket) => ({
      intervalLabel: formatLocalTime(bucket.bucketStart, BUCKET_LABEL),
      estimatedCostUsd: bucket.costUsd,
    })),
    averageLatency: apiData.averageLatency.map((bucket) => ({
      intervalLabel: formatLocalTime(bucket.bucketStart, BUCKET_LABEL),
      avgLatencyMs: bucket.avgDurationMs,
    })),
    requestStatusBreakdown,
  };
}

export function mapTelemetryRow(row: AnalyticsTelemetryApiRow): TelemetrySession {
  return {
    sessionId: row.sessionId,
    userName: row.userName,
    userId: row.userId,
    entityName: row.entityName,
    model: row.model,
    status: API_STATUS_TO_TELEMETRY_STATUS[row.status],
    messageCount: row.messageCount,
    totalTokens: row.totalTokens,
    estimatedCostUsd: row.estimatedCostUsd,
    durationMs: row.durationMs,
    avgLatencyMs: row.avgLatencyMs,
    promptStartAt: row.promptStartAt,
    promptEndAt: row.promptEndAt,
  };
}

export function mapTelemetryPage(apiData: AnalyticsTelemetryApiResponse): {
  rows: TelemetrySession[];
  totalCount: number;
  totalPages: number;
} {
  return {
    rows: apiData.rows.map(mapTelemetryRow),
    totalCount: apiData.totalCount,
    totalPages: apiData.totalPages,
  };
}

export function mapConversationInteractionStatus(
  row: AnalyticsConversationInteractionRow,
): TelemetryStatus {
  return API_STATUS_TO_TELEMETRY_STATUS[row.status];
}

// ── Date range ───────────────────────────────────────────────────────────────

export type AnalyticsDateRangePreset = '7d' | '30d' | 'custom' | 'all';

export interface AnalyticsDateRangeValue {
  preset: AnalyticsDateRangePreset;
  range: AnalyticsDateRange;
}

/**
 * `preset === "all"` means "send no startDate/endDate"; every other preset
 * sends both. `/overview` has a server-side 30-day default, while `/telemetry`
 * has no default range.
 */
export function resolveDateRangeParams(value: AnalyticsDateRangeValue): {
  startDate?: string;
  endDate?: string;
} {
  if (value.preset === 'all') {
    return {};
  }

  return { startDate: value.range.startDate, endDate: value.range.endDate };
}

export function rangeForPreset(preset: '7d' | '30d', now: Date = new Date()): AnalyticsDateRange {
  const startDate = new Date(now);
  startDate.setDate(startDate.getDate() - (preset === '7d' ? 6 : 29));
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(now);
  endDate.setHours(23, 59, 59, 999);

  return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
}

export function getDefaultAnalyticsDateRangeValue(now: Date = new Date()): AnalyticsDateRangeValue {
  return { preset: '30d', range: rangeForPreset('30d', now) };
}

/**
 * The local calendar day of an instant, as `<input type="date">` and the shareable
 * URL want it. `toISOString().slice(0, 10)` hands back the UTC day — a day earlier
 * for a local midnight east of Greenwich, a day later for a local end-of-day west of it.
 */
export function toLocalDateValue(iso: string): string {
  const date = new Date(iso);
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}

/** A local start-of-day (`startDate`) or end-of-day (`endDate`) boundary for a `YYYY-MM-DD` value. */
export function toIsoBoundary(raw: string, field: 'endDate' | 'startDate'): string {
  const date = new Date(`${raw}T00:00:00`);

  if (field === 'startDate') {
    date.setHours(0, 0, 0, 0);
  } else {
    date.setHours(23, 59, 59, 999);
  }

  return date.toISOString();
}

// ── Errors ───────────────────────────────────────────────────────────────────

export interface AnalyticsErrorInfo {
  isDbUnavailable: boolean;
  message: string;
}

export const ANALYTICS_DB_UNAVAILABLE_MESSAGE =
  'The database is temporarily unavailable. Please try again shortly.';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * A 503 with `code: "DB_UNAVAILABLE"` is the one failure the analytics API
 * distinguishes; every other non-2xx is a generic failure. Machine text never
 * leaves this function — an unrecognised body gets the generic sentence.
 */
export function getAnalyticsErrorInfo(error: unknown): AnalyticsErrorInfo {
  if (!isRecord(error)) {
    return { isDbUnavailable: false, message: 'Something went wrong. Try again.' };
  }

  const body = error.data;

  if (error.status === 503 && isRecord(body) && body.code === 'DB_UNAVAILABLE') {
    return { isDbUnavailable: true, message: ANALYTICS_DB_UNAVAILABLE_MESSAGE };
  }

  if (isRecord(body) && typeof body.error === 'string' && body.error) {
    return { isDbUnavailable: false, message: body.error };
  }

  // Anything else is the transport's own text ("Request failed with status code 500",
  // a raw HTML body, "Network Error"). That is machine output; the page shows a
  // sentence and the detail stays in the console.
  return { isDbUnavailable: false, message: 'Something went wrong. Try again.' };
}
